import { z } from 'zod';

/**
 * The site-content contract: what Desk saves, what a publish snapshots, and
 * what the site build reads. One file, imported by the site (src/data/work.ts
 * parses the snapshot with it), by Desk (the editor validates before saving)
 * and, through scripts/sync-shared.mjs, by the Edge Functions.
 *
 * The shapes mirror the hand-written, heavily commented interfaces in
 * src/data/types.ts, which stay the documentation. The type assertions at
 * the bottom fail `astro check` the moment the two disagree.
 *
 * Dependency-free apart from zod (see sync-shared.mjs for why).
 */

const text = z.string().trim().min(1);
const slug = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers and single hyphens only.');

export const seoSchema = z.object({
  title: text.max(70, 'Keep the title under 70 characters; Google cuts it near 60.'),
  description: z.string().trim().min(50).max(170, 'Keep the description under 170 characters.'),
  ogImage: z.string().optional(),
});

export const pointSchema = z.object({ head: text, tail: text });

export const projectSchema = z.object({
  id: slug,
  slug,
  name: text,
  industry: text,
  blurb: text,
  role: text,
  year: z.string().regex(/^\d{4}$/, 'A four-digit year.'),
  url: z.string().url().optional(),
  domain: z.string().optional(),
  live: z.boolean().optional(),
  /** A site path without extension: /work/x (committed) or /media/work/x (from Desk). */
  image: z.string().regex(/^\/[a-z0-9/_-]+$/i, 'A site path with no extension, like /media/work/acme.'),
  stack: z.array(text),
  services: z.array(slug).min(1, 'Pick at least one service this project demonstrates.'),
  seo: seoSchema,
  story: z.object({
    brief: z.array(text).min(1),
    built: z.array(z.object({ title: text, body: text })).min(1),
    decisions: z.array(pointSchema),
  }),
  testimonial: z.string().optional(),
});

export const testimonialSchema = z.object({
  id: z.string().min(1),
  quote: text,
  company: text,
  descriptor: text,
  project: slug,
});

export const labItemSchema = z.object({
  id: slug,
  name: text,
  blurb: text,
  status: text,
  notes: z.array(text),
  stack: z.array(text),
  evidence: z.object({ label: text, href: z.string().url(), note: text }).optional(),
  schematic: z
    .object({ src: text, caption: text, width: z.number().int().positive(), height: z.number().int().positive() })
    .optional(),
});

export const redirectSchema = z.object({
  from: z.string().startsWith('/'),
  to: z.string().startsWith('/'),
  status: z.union([z.literal(301), z.literal(302)]),
});

/**
 * Everything one publish puts on the site. `hidden*` lists ids that exist but
 * are switched off, so a service page that names one can skip it rather than
 * fail the build; an id in neither list is a typo and still fails.
 */
export const siteContentSchema = z.object({
  version: z.literal(1),
  /** The publish this came from; null for content that never went through Desk. */
  publishId: z.number().int().nullable(),
  projects: z.array(projectSchema),
  hiddenProjects: z.array(z.string()),
  testimonials: z.array(testimonialSchema),
  lab: z.array(labItemSchema),
  hiddenLab: z.array(z.string()),
  redirects: z.array(redirectSchema),
});

export type SiteContent = z.infer<typeof siteContentSchema>;
export type ProjectInput = z.infer<typeof projectSchema>;
export type LabItemInput = z.infer<typeof labItemSchema>;
export type TestimonialInput = z.infer<typeof testimonialSchema>;
